import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalText from '../common/LocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import StatusTabs from '../approval/StatusTabs';
import ApprovalStatusBadge from '../approval/ApprovalStatusBadge';
import ActionTypeChip from '../approval/ActionTypeChip';
import SubmitForApprovalButton from '../approval/SubmitForApprovalButton';
import WithdrawButton from '../approval/WithdrawButton';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import ScheduleGameModal from './ScheduleGameModal';
import QuizParticipantsModal from './QuizParticipantsModal';
import { LockedTag, PhaseChip } from './QuizChips';
import { resolveGameName, filterLabelSx, selectSx } from '../gameScoreBoard/ScoreboardFilterBar';
import { useToast } from '../../context/ToastContext';
import { useLocalization } from '../../context/LocalizationContext';
import { useApproval } from '../../context/ApprovalContext';
import { useSpaceId } from '../../hooks/useSpaceId';
import { getGames } from '../../api/gameScoreBoardApi';
import {
  createQuiz, deleteQuiz, getQuiz, getQuizzes, updateQuiz,
} from '../../api/gameScheduleApi';
import { submitApproval } from '../../api/approvalApi';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import { MODAL_MODES, SCHEDULE_TYPE_LABEL, isScheduledGame } from '../../constants/gameSchedule';
import { buildVersionedList, countVersionedTabs, filterVersionedList } from '../../utils/versionedContent';
import { todayEastern } from '../../utils/dateUtils';
import {
  formatPeriodRange, phaseOf, rowState, toSiblingRanges,
} from '../../utils/gameSchedule';
import { C, btnBase, ctaNavy } from '../../theme/adminTokens';

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontSize: 13,
  color: '#1e293b',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  py: 1.5,
  px: 2,
  verticalAlign: 'middle',
};

const outlineBtnSx = {
  ...btnBase,
  fontSize: 11.5,
  borderColor: C.border,
  color: C.navy,
  whiteSpace: 'nowrap',
  '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' },
};

const TAB_LABEL = { live: 'live', pending: 'pending', draft: 'draft', rejected: 'rejected' };

const STARTED_TIP = "Started quizzes can't be edited";
const STARTED_DELETE_NOTE = 'This quiz has already started. Once approved, the points of fans who have already played it will be removed.';

const winnersTip = (announcementName) =>
  `Winners were already granted for the announcement "${announcementName}" that uses this quiz`;

// What the "Propose Deletion" confirmation says. A started quiz only needs the points warning (the approval step is
// the same for every quiz); a linked announcement is always named: it is deleted together with its only quiz, or just
// loses this quiz when it has others.
const proposeDeleteSubtitle = ({ row, linkedAnnouncement }) => {
  const others = (linkedAnnouncement?.quizCount ?? 1) - 1;
  let announcement = null;
  if (linkedAnnouncement && others > 0) {
    announcement = `This quiz is in the announcement "${linkedAnnouncement.name}". It is removed from it once a marketing manager approves; the announcement keeps ${others === 1 ? 'its other quiz' : `its other ${others} quizzes`}. The quiz stays live until then.`;
  } else if (linkedAnnouncement) {
    announcement = `This quiz is linked to the announcement "${linkedAnnouncement.name}". It is deleted together with the quiz once a marketing manager approves. The quiz stays live until then.`;
  }
  if (row.isLocked) return announcement ? `${announcement} ${STARTED_DELETE_NOTE}` : STARTED_DELETE_NOTE;
  return announcement ?? 'A marketing manager has to approve the deletion. The quiz stays live until then.';
};
const PENDING_TIP = 'A pending proposal exists — View it or Withdraw first';
const REJECTED_TIP = 'A rejected proposal exists — View it or Dismiss first';

/**
 * Game Schedule (plan 13.2): a game select, the Schedule Game button, the Live / Pending / Draft / Rejected tabs and the
 * table of the space's quizzes with their row actions. Same approval state machine as the other content types: a draft
 * with no approval record is edited/deleted directly and submitted from its row; a live quiz that has not started is
 * changed only through proposals; a started quiz can no longer be edited, only deleted through a delete proposal (which
 * also takes the fans' points back); pending and rejected proposals get their own row under
 * their tab (versionedContent) while the live row stays under Live.
 */
export default function GameScheduleSection({ canManage = false }) {
  const { toast } = useToast();
  const { locale } = useLocalization();
  const { addMySubmission } = useApproval();
  const spaceId = useSpaceId();

  // ── Games of the space that use schedules ─────────────────────────────────────────────────────────────────
  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gameId, setGameId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setGamesLoading(true);
    getGames()
      .then((data) => {
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : []).filter(isScheduledGame);
        setGames(list);
        setGameId((current) => list.find((g) => g.id === current)?.id ?? (list.find((g) => g.isActive) ?? list[0])?.id ?? null);
      })
      .catch(() => { if (!cancelled) { setGames([]); setGameId(null); } })
      .finally(() => { if (!cancelled) setGamesLoading(false); });
    return () => { cancelled = true; };
  }, [spaceId]);

  const game = games.find((g) => g.id === gameId) ?? null;
  const gameName = game ? resolveGameName(game, locale) : 'Quiz';

  // ── Quizzes ───────────────────────────────────────────────────────────────────────────────────────────────
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const requestRef = useRef(0);

  const refresh = useCallback(() => {
    if (!gameId) {
      setQuizzes([]);
      setLoading(false);
      setLoadFailed(false);
      return Promise.resolve();
    }
    requestRef.current += 1;
    const mine = requestRef.current;
    setLoading(true);
    setLoadFailed(false);
    return getQuizzes()
      .then((data) => { if (mine === requestRef.current) setQuizzes(Array.isArray(data) ? data : []); })
      .catch(() => { if (mine === requestRef.current) setLoadFailed(true); })
      .finally(() => { if (mine === requestRef.current) setLoading(false); });
  }, [gameId]);

  useEffect(() => { refresh(); }, [refresh]);

  const [statusTab, setStatusTab] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flash = (tab) => setHighlight({ tab, token: Date.now() });

  const versioned = useMemo(() => buildVersionedList(quizzes, 'id'), [quizzes]);
  const counts = useMemo(() => countVersionedTabs(quizzes, 'id'), [quizzes]);
  const rows = useMemo(() => filterVersionedList(versioned, statusTab), [versioned, statusTab]);

  const today = todayEastern();

  // ── Popups and confirmations ──────────────────────────────────────────────────────────────────────────────
  const [modal, setModal] = useState(null);                 // { mode, quiz, quizId, resubmitAs }
  const [approvalView, setApprovalView] = useState(null);   // the row whose proposal is shown in the compare view
  const [participantsFor, setParticipantsFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);   // { row, propose, linkedAnnouncement }
  // List rows carry { id, name, hasWinners } of their announcement. When the API did not send it (or a grant happened
  // since the list loaded) the winners lock is learned from the quiz detail when it is opened, and remembered here
  // ({ [quizId]: announcement name }) so its buttons stay disabled.
  const [winnersLocked, setWinnersLocked] = useState({});

  const handleGameChange = (next) => {
    setGameId(next);
    setModal(null);
    setApprovalView(null);
    setParticipantsFor(null);
    setDeleteTarget(null);
  };

  const openCreate = () => setModal({ mode: MODAL_MODES.CREATE, quiz: null, quizId: null });

  const noteWinners = (id, announcementName) => {
    setWinnersLocked((m) => ({ ...m, [id]: announcementName }));
    toast.error(winnersTip(announcementName));
  };

  // The list has no questions: a quiz to edit or view is read in full first. A quiz that vanished meanwhile (404) is
  // toasted by the API interceptor; the list is refreshed so the row disappears too.
  const openDetail = async (id, mode) => {
    try {
      const detail = await getQuiz(id);
      if (mode === MODAL_MODES.PROPOSE && detail?.linkedAnnouncement?.hasWinners) {
        noteWinners(id, detail.linkedAnnouncement.name);
        return;
      }
      setModal({ mode, quiz: detail, quizId: id });
    } catch {
      refresh();
    }
  };

  const liveIdOf = (row) => row._liveId ?? row.id;

  // The name of the announcement with granted winners that uses this quiz (its quiz can then no longer be edited or deleted).
  const winnersAnnouncementOf = (row) => (row.linkedAnnouncement?.hasWinners ? row.linkedAnnouncement.name : (winnersLocked[liveIdOf(row)] ?? null));

  const approvalRecordOf = (row) => ({
    approvalId: row.approvalId,
    actionType: row.approvalActionType,
    contentType: CONTENT_TYPES.GAME_SCHEDULE,
    sectionLabel: 'Game Schedule',
    contentName: row.name,
    submittedByName: '',
    submittedAt: null,
    status: row.approvalStatus,
  });

  const handleView = (row) => {
    const state = rowState(row);
    if (state === 'pending-proposal' || state === 'rejected-proposal' || state === 'pending-create') setApprovalView(row);
    else openDetail(liveIdOf(row), MODAL_MODES.VIEW);
  };

  // Edit & Resubmit of a rejected proposal. The compare view hands over the proposed snapshot; if it is missing the
  // live quiz is read instead.
  const openResubmit = async (row, snapshot) => {
    const id = liveIdOf(row);
    setApprovalView(null);
    let quiz = snapshot ?? (Array.isArray(row.questions) ? row : null);
    if (!quiz) {
      try { quiz = await getQuiz(id); } catch { refresh(); return; }
    }
    setModal({ mode: MODAL_MODES.RESUBMIT, quiz, quizId: id, resubmitAs: row.approvalActionType, baselineQuiz: null });

    // A rejected EDIT is measured against the live quiz (its period and linked announcement), not against the proposal's
    // snapshot, which already carries the proposed dates. The popup is already open; the live quiz is handed over when read.
    if (row.approvalActionType !== 'create') {
      try {
        const live = await getQuiz(id);
        setModal((m) => (m && m.mode === MODAL_MODES.RESUBMIT && m.quizId === id ? { ...m, baselineQuiz: live } : m));
      } catch {
        // no baseline: the server still asks for the announcement date if the period changed
      }
    }
  };

  // Deleting a live quiz is a proposal; when a live announcement is linked the confirmation names it (it is deleted with
  // the quiz, or only loses it when it has other quizzes). The server finds the announcement itself, this only informs the marketer.
  const openDelete = async (row, propose) => {
    setDeleteTarget({ row, propose, linkedAnnouncement: null });
    if (!propose) return;
    let linkedAnnouncement = null;
    try { linkedAnnouncement = (await getQuiz(liveIdOf(row)))?.linkedAnnouncement ?? null; } catch { linkedAnnouncement = null; }
    if (linkedAnnouncement?.hasWinners) {
      setDeleteTarget((t) => (t && t.row === row ? null : t));
      noteWinners(liveIdOf(row), linkedAnnouncement.name);
      return;
    }
    if (linkedAnnouncement) setDeleteTarget((t) => (t && t.row === row ? { ...t, linkedAnnouncement } : t));
  };

  const handleSave = async (payload) => {
    const { mode, quizId, resubmitAs } = modal;

    if (mode === MODAL_MODES.CREATE) {
      await createQuiz(payload);
      toast.success('Quiz saved as a draft.');
      flash('draft');
    } else if (mode === MODAL_MODES.EDIT) {
      await updateQuiz(quizId, payload);
      toast.success('Quiz updated.');
    } else if (mode === MODAL_MODES.PROPOSE) {
      const result = await updateQuiz(quizId, payload);
      toast.success(result.message || 'Edit proposal submitted for approval.');
      flash('pending');
    } else if (mode === MODAL_MODES.RESUBMIT) {
      if (resubmitAs === 'create') {
        // A rejected new quiz is still a draft: save the changes, then send it for approval again.
        await updateQuiz(quizId, payload);
        try {
          const submitted = await submitApproval({
            contentType: CONTENT_TYPES.GAME_SCHEDULE, contentId: quizId, contentName: payload.name, actionType: 'create',
          });
          addMySubmission(submitted?.approvalId);
        } catch (err) {
          await refresh(); // the changes are saved even though the submission failed
          throw err;
        }
        toast.success('Resubmitted for approval!');
      } else {
        const result = await updateQuiz(quizId, payload);
        toast.success(result.message || 'Edit proposal resubmitted for approval.');
      }
      flash('pending');
    }

    setModal(null);
    await refresh();
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      const result = await deleteQuiz(target.row.id);
      if (result.proposed) {
        toast.success(result.message || 'Delete proposal submitted for approval.');
        flash('pending');
      } else {
        toast.success('Quiz deleted.');
      }
    } catch {
      // The API interceptor toasts the reason (e.g. a proposal is already pending); the list is refreshed below.
    } finally {
      await refresh();
    }
  };

  // A quiz whose period is over can never be played: it must get new dates before it goes to an approver.
  const blockEndedSubmit = (row) => {
    if (phaseOf(row, today) !== 'ended') return false;
    toast.error("This quiz's period has already ended. Change its dates before submitting it for approval.");
    return true;
  };

  // ── Row rendering ─────────────────────────────────────────────────────────────────────────────────────────
  const renderActions = (row) => {
    const state = rowState(row);
    const live = state === 'live' || state === 'live-with-proposal' || state === 'live-locked';
    const name = row.name || `Quiz #${row.id}`;
    const winnersName = live ? winnersAnnouncementOf(row) : null;
    const winnersDisabledTip = winnersName ? winnersTip(winnersName) : null;

    const viewButton = (
      <Tooltip title="View">
        <IconButton size="small" onClick={() => handleView(row)} aria-label={`View ${name}`}>
          <VisibilityOutlinedIcon sx={{ fontSize: 17, color: '#3b82f6' }} />
        </IconButton>
      </Tooltip>
    );

    const participantsButton = live && (
      <Button
        size="small"
        variant="outlined"
        startIcon={<EmojiEventsOutlinedIcon sx={{ fontSize: 14 }} />}
        onClick={() => setParticipantsFor(row)}
        sx={outlineBtnSx}
      >
        <LocalText>View Participants</LocalText>
      </Button>
    );

    // disabledTip disables both buttons; deleteDisabledTip overrides it for Delete (null = Delete stays enabled).
    const editDeletePair = (editTip, deleteTip, onEdit, onDelete, disabledTip, deleteDisabledTip = disabledTip) => (
      <>
        <Tooltip title={disabledTip ?? editTip}>
          <span>
            <IconButton size="small" onClick={onEdit} disabled={Boolean(disabledTip)} aria-label={editTip}>
              <EditOutlinedIcon sx={{ fontSize: 17, color: disabledTip ? undefined : C.muted }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={deleteDisabledTip ?? deleteTip}>
          <span>
            <IconButton size="small" onClick={onDelete} disabled={Boolean(deleteDisabledTip)} aria-label={deleteTip}>
              <DeleteOutlineIcon sx={{ fontSize: 17, color: deleteDisabledTip ? undefined : C.muted }} />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );

    let buttons;
    switch (state) {
      case 'draft':
        buttons = canManage ? (
          <>
            {editDeletePair(
              'Edit quiz', 'Delete quiz',
              () => openDetail(row.id, MODAL_MODES.EDIT),
              () => openDelete(row, false),
            )}
            <SubmitForApprovalButton
              contentType={CONTENT_TYPES.GAME_SCHEDULE}
              contentId={row.id}
              contentName={name}
              onBeforeSubmit={() => blockEndedSubmit(row)}
              onSubmitted={() => { refresh(); flash('pending'); }}
            />
          </>
        ) : viewButton;
        break;

      case 'pending-create':
      case 'pending-proposal':
        buttons = (
          <>
            {viewButton}
            {canManage && row.approvalId && (
              <WithdrawButton
                approvalId={row.approvalId}
                onWithdrawn={() => { refresh(); flash(row.approvalActionType === 'create' ? 'draft' : 'live'); }}
              />
            )}
          </>
        );
        break;

      case 'rejected-create':
        buttons = viewButton;
        break;

      case 'rejected-proposal':
        buttons = (
          <>
            {viewButton}
            {canManage && row.approvalActionType !== 'delete' && (
              <Button size="small" variant="outlined" onClick={() => openResubmit(row, null)} sx={outlineBtnSx}>
                <LocalText>Edit &amp; Resubmit</LocalText>
              </Button>
            )}
            {canManage && row.approvalId && (
              <WithdrawButton
                approvalId={row.approvalId}
                variant="dismiss"
                onWithdrawn={() => { refresh(); flash(row.approvalActionType === 'create' ? 'draft' : 'live'); }}
              />
            )}
          </>
        );
        break;

      case 'live-locked':
        buttons = (
          <>
            {viewButton}
            {canManage && editDeletePair(
              'Edit quiz', 'Propose delete', undefined, () => openDelete(row, true), STARTED_TIP, winnersDisabledTip,
            )}
          </>
        );
        break;

      case 'live-with-proposal':
        buttons = (
          <>
            {viewButton}
            {canManage && editDeletePair(
              'Propose edit', 'Propose delete', undefined, undefined,
              row.approvalStatus === 'pending' ? PENDING_TIP : REJECTED_TIP,
            )}
          </>
        );
        break;

      case 'live':
      default:
        buttons = (
          <>
            {viewButton}
            {canManage && editDeletePair(
              'Propose edit', 'Propose delete',
              () => openDetail(row.id, MODAL_MODES.PROPOSE),
              () => openDelete(row, true),
              winnersDisabledTip,
            )}
          </>
        );
        break;
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 0.75 }}>
        {participantsButton}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
          {buttons}
        </Box>
      </Box>
    );
  };

  const emptyMessage = (() => {
    if (!gameId && !gamesLoading) return "This space doesn't have a scheduled game yet. Ask the team to give it the Quiz game.";
    if (quizzes.length === 0) return 'No quizzes scheduled yet for this game.';
    return `No ${TAB_LABEL[statusTab] ?? statusTab} quizzes.`;
  })();

  const siblingsFor = modal ? toSiblingRanges(quizzes, modal.quizId) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography id="schedule-game-label" sx={filterLabelSx}>
            <LocalText>Game</LocalText>
          </Typography>
          <FormControl size="small" disabled={gamesLoading || games.length === 0} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={gameId ?? ''}
              onChange={(e) => handleGameChange(e.target.value)}
              displayEmpty
              sx={selectSx}
              labelId="schedule-game-label"
              inputProps={{ 'aria-label': 'Select a game' }}
              renderValue={(v) => {
                if (!v) return <Typography sx={{ color: '#94a3b8', fontSize: 13 }}><LocalText>Select a game</LocalText></Typography>;
                const g = games.find((item) => item.id === v);
                return <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{resolveGameName(g, locale)}</Typography>;
              }}
            >
              {games.map((g) => (
                <MenuItem key={g.id} value={g.id}>{resolveGameName(g, locale)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Tooltip title={canManage ? '' : 'You can view the schedule, but not change it'}>
          <Box component="span" sx={{ width: { xs: '100%', sm: 'auto' }, display: 'inline-flex' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={openCreate}
              disabled={!gameId || !canManage}
              sx={{ ...btnBase, ...ctaNavy, width: { xs: '100%', sm: 'auto' } }}
            >
              <LocalText>Schedule Game</LocalText>
            </Button>
          </Box>
        </Tooltip>
      </Box>

      <StatusTabs
        items={quizzes}
        counts={counts}
        value={statusTab}
        onChange={setStatusTab}
        statusKey="approvalStatus"
        highlightTab={highlight.tab}
        highlightToken={highlight.token}
      />

      {loadFailed && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={refresh}><LocalText>Retry</LocalText></Button>}
          sx={{ borderRadius: 2, mb: 2 }}
        >
          <LocalText>Couldn't load the quizzes.</LocalText>
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2" role="status" sx={{ color: C.muted }}>
          <LocalText>Loading…</LocalText>
        </Typography>
      ) : rows.length === 0 ? (
        !loadFailed && (
          <Typography variant="body2" role="status" sx={{ color: C.muted }}>
            <LocalText>{emptyMessage}</LocalText>
          </Typography>
        )
      ) : (
        <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
          <Table
            size="small"
            aria-label="Game schedule"
            sx={{
              // Fixed column widths once there is room; on a phone auto layout with a real minimum width, so the
              // container scrolls horizontally instead of squeezing the actions into the next column.
              tableLayout: { xs: 'auto', md: 'fixed' },
              minWidth: { xs: 640, md: 'auto' },
              '& tbody tr:last-of-type td': { borderBottom: 'none' },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                <TableCell scope="col" sx={{ ...headCellSx, width: '20%' }} align="center"><LocalText>Name</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '23%' }} align="center"><LocalText>Period</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '12%' }} align="center"><LocalText>Questions</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '14%' }} align="center"><LocalText>Status</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '31%' }} align="center"><LocalText>Actions</LocalText></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const snapshot = row._viewType === 'snapshot';
                const type = String(row.scheduleType ?? '').toLowerCase();
                const phase = snapshot ? phaseOf(row, today) : (row.phase ?? phaseOf(row, today));
                const questionCount = snapshot ? (row.questions?.length ?? row.questionCount ?? 0) : (row.questionCount ?? 0);
                const state = rowState(row);
                return (
                  <TableRow
                    key={snapshot ? `snapshot-${row.id}` : row.id}
                    hover
                    data-testid={snapshot ? 'quiz-row-snapshot' : 'quiz-row'}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' }, ...(snapshot && { bgcolor: '#fffdf5' }) }}
                  >
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700, overflowWrap: 'break-word', minWidth: { xs: 150, md: 0 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        <span>{row.name || `Quiz #${row.id}`}</span>
                        {snapshot && <ActionTypeChip actionType={row.approvalActionType} />}
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                          <LocalText>{SCHEDULE_TYPE_LABEL[type] ?? String(row.scheduleType ?? '')}</LocalText>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: 12, color: C.muted }}>
                          {formatPeriodRange(row.startDate, row.endDate)}
                        </Typography>
                        <PhaseChip phase={phase} />
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="center">{questionCount}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        {/* A not-yet-approved quiz keeps contentStatus 'draft' while its create is in review, so its chip follows the approval */}
                        <ApprovalStatusBadge
                          status={snapshot || state === 'pending-create' || state === 'rejected-create' ? row.approvalStatus : row.contentStatus}
                          actionType={row.approvalActionType}
                        />
                        {row.isLocked && !snapshot && (state === 'live-locked' || state === 'live-with-proposal') && <LockedTag />}
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{renderActions(row)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {modal && (
        <ScheduleGameModal
          mode={modal.mode}
          gameName={gameName}
          pointsPerCorrect={game?.pointsPerCorrect ?? null}
          initialQuiz={modal.quiz}
          baselineQuiz={modal.baselineQuiz ?? null}
          siblings={siblingsFor}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {participantsFor && (
        <QuizParticipantsModal quiz={participantsFor} onClose={() => setParticipantsFor(null)} />
      )}

      {approvalView && (
        <ContentPreviewDrawer
          open
          readOnly
          approvalRecord={approvalRecordOf(approvalView)}
          onClose={() => setApprovalView(null)}
          onEdit={canManage && approvalView.approvalStatus === 'rejected' && approvalView.approvalActionType !== 'delete'
            ? (snapshot) => openResubmit(approvalView, snapshot)
            : undefined}
        />
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        severity={deleteTarget?.propose ? 'warning' : 'danger'}
        title={deleteTarget?.propose ? 'Propose Deletion' : 'Delete Quiz'}
        message={deleteTarget
          ? (deleteTarget.propose
            ? `Propose deleting "${deleteTarget.row.name}"?`
            : `Delete "${deleteTarget.row.name}"?`)
          : ''}
        subtitle={deleteTarget?.propose
          ? proposeDeleteSubtitle(deleteTarget)
          : 'This removes the quiz and cannot be undone.'}
        confirmLabel={deleteTarget?.propose ? 'Submit proposal' : 'Delete'}
      />
    </Box>
  );
}
